import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { FileText, X, Loader2, AlertCircle, BookmarkPlus } from 'lucide-react';
import { useCreateJournalEntry } from '@/hooks/useJournal';
import { useJournalTemplates, useCreateJournalTemplate } from '@/hooks/useJournalTemplates';
import { useSetVisitStatus } from '@/hooks/useAppointments';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';

export interface JournalModalProps {
  patientId: string;
  clinicId: string;
  appointmentId?: string;
  appointmentDate?: Date;
  appointmentVisitStatus?: string | null;
  onClose: () => void;
  onCreated?: () => void;
}

interface SaveAsTemplateState {
  title: string;
  category: string;
  scope: 'patient' | 'clinic';
}

const defaultSaveAsTemplate = (): SaveAsTemplateState => ({
  title: '',
  category: '',
  scope: 'patient',
});

export function JournalModal({
  patientId,
  clinicId,
  appointmentId,
  appointmentDate,
  appointmentVisitStatus,
  onClose,
  onCreated,
}: JournalModalProps) {
  const { t } = useTranslation();
  const { isMainClinicAdmin, isPlatformAdmin } = useAuth();
  const isAdmin = isMainClinicAdmin || isPlatformAdmin;

  const createEntry = useCreateJournalEntry(clinicId);
  const createTemplate = useCreateJournalTemplate(clinicId);
  const setVisitStatus = useSetVisitStatus();
  const { data: templates } = useJournalTemplates(clinicId, patientId);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState<SaveAsTemplateState>(defaultSaveAsTemplate);

  const patientTemplates = (templates ?? []).filter((t) => t.patient_id === patientId);
  const clinicTemplates = (templates ?? []).filter((t) => t.patient_id === null);

  // Handle template selection
  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) {
      setTitle('');
      setContent('');
      return;
    }
    const template = templates?.find((t) => t.id === templateId);
    if (template) {
      setTitle(template.title);
      setContent(template.content_md);
    }
  };

  const handleOpenSaveAsTemplate = () => {
    setSaveAsTemplate((prev) => ({ ...prev, title: title }));
    setShowSaveAsTemplate(true);
  };

  const handleSaveTemplate = async () => {
    const templateTitle = saveAsTemplate.title.trim();
    if (!templateTitle) return;
    try {
      await createTemplate.mutateAsync({
        title: templateTitle,
        content_md: content,
        category: saveAsTemplate.category.trim() || undefined,
        patient_id: saveAsTemplate.scope === 'patient' ? patientId : null,
      });
      toast.success(t('journal.template_saved', 'Template saved'));
      setShowSaveAsTemplate(false);
      setSaveAsTemplate(defaultSaveAsTemplate());
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!content.trim()) {
      setError(t('journal.errors.content_required', 'Indhold er påkrævet'));
      return;
    }

    try {
      setError(null);
      await createEntry.mutateAsync({
        patient_id: patientId,
        title: title.trim() || undefined,
        content_md: content.trim(),
        appointment_id: appointmentId,
      });

      // Auto-advance visit status to "under_treatment" when journal is opened
      if (appointmentId && appointmentVisitStatus !== 'under_treatment' &&
          appointmentVisitStatus !== 'treatment_finished' && appointmentVisitStatus !== 'payment_completed') {
        void setVisitStatus.mutateAsync({ appointment_id: appointmentId, visit_status: 'under_treatment' }).catch(() => {
          // Non-critical: don't block journal creation if status update fails
        });
      }

      toast.success(t('journal.toasts.created', 'Journalnotat oprettet'));
      onCreated?.();
      onClose();
    } catch (err) {
      console.error('Error creating journal entry:', err);
      setError(t('journal.errors.create_failed', 'Kunne ikke oprette journalnotat'));
    }
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-black/50 p-4">
      <div className="mx-auto mt-10 max-w-3xl rounded-xl bg-white shadow-xl dark:bg-card-dark">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {t('journal.modal.title', 'Nyt journalnotat')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
            aria-label={t('common.close', 'Luk')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                    {error}
                  </h3>
                </div>
              </div>
            </div>
          )}

          {appointmentDate && (
            <div className="mb-4 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {t('journal.modal.appointment_context', 'Journalnotat for aftale den')}{' '}
                {appointmentDate.toLocaleDateString(
                  t('common.locale', 'da-DK'),
                  { day: 'numeric', month: 'long', year: 'numeric' }
                )}
              </p>
            </div>
          )}

          {/* Template Selector */}
          {templates && templates.length > 0 && (
            <div className="mb-4">
              <label
                htmlFor="template"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
              >
                {t('journal.modal.template', 'Skabelon')}
              </label>
              <select
                id="template"
                value={selectedTemplateId}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900
                  focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 
                  dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                disabled={createEntry.isPending}
              >
                <option value="">
                  {t('journal.modal.template_none', 'Ingen skabelon')}
                </option>
                {patientTemplates.length > 0 && (
                  <optgroup label={t('journal.patient_templates', 'Patient templates')}>
                    {patientTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.title}
                      </option>
                    ))}
                  </optgroup>
                )}
                {clinicTemplates.length > 0 && (
                  <optgroup label={t('journal.clinic_templates', 'Clinic templates')}>
                    {clinicTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.title}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
          )}

          {/* Title */}
          <div className="mb-4">
            <label
              htmlFor="title"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
            >
              {t('journal.modal.title_field', 'Titel')} ({t('common.optional', 'valgfri')})
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('journal.modal.title_placeholder', 'F.eks. Konsultation, Behandling, etc.')}
              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900
                focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 
                dark:border-gray-600 dark:bg-gray-700 dark:text-white
                placeholder:text-gray-400 dark:placeholder:text-gray-500"
              disabled={createEntry.isPending}
            />
          </div>

          {/* Content */}
          <div className="mb-4">
            <label
              htmlFor="content"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
            >
              {t('journal.modal.content', 'Indhold')}
            </label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={12}
              placeholder={t('journal.modal.content_placeholder', 'Skriv journalnotat her...\n\nDu kan bruge Markdown formatering:\n# Overskrift\n**Fed tekst**\n- Punktliste')}
              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono text-gray-900
                focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 
                dark:border-gray-600 dark:bg-gray-700 dark:text-white
                placeholder:text-gray-400 dark:placeholder:text-gray-500"
              disabled={createEntry.isPending}
              required
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t('journal.modal.markdown_tip', 'Markdown formatering understøttes')}
            </p>
          </div>

          {/* Save as Template panel */}
          {showSaveAsTemplate ? (
            <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 p-3 dark:border-indigo-800 dark:bg-indigo-950/40">
              <p className="mb-2 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                {t('journal.save_as_template', 'Save as template')}
              </p>
              <div className="space-y-2">
                <input
                  className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm
                    focus:border-indigo-500 focus:outline-none focus:ring-indigo-500
                    dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  placeholder={t('journal.template_name_placeholder', 'Template name')}
                  value={saveAsTemplate.title}
                  onChange={(e) =>
                    setSaveAsTemplate((prev) => ({ ...prev, title: e.target.value }))
                  }
                />
                <input
                  className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm
                    focus:border-indigo-500 focus:outline-none focus:ring-indigo-500
                    dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  placeholder={t('journal.template_category_placeholder', 'Category (optional)')}
                  value={saveAsTemplate.category}
                  onChange={(e) =>
                    setSaveAsTemplate((prev) => ({ ...prev, category: e.target.value }))
                  }
                />
                <div className="flex gap-4 text-sm">
                  <label className="flex cursor-pointer items-center gap-1.5 text-gray-700 dark:text-gray-200">
                    <input
                      type="radio"
                      name="templateScopeModal"
                      value="patient"
                      checked={saveAsTemplate.scope === 'patient'}
                      onChange={() =>
                        setSaveAsTemplate((prev) => ({ ...prev, scope: 'patient' }))
                      }
                      className="accent-indigo-600"
                    />
                    {t('journal.template_scope_patient', 'This patient only')}
                  </label>
                  {isAdmin && (
                    <label className="flex cursor-pointer items-center gap-1.5 text-gray-700 dark:text-gray-200">
                      <input
                        type="radio"
                        name="templateScopeModal"
                        value="clinic"
                        checked={saveAsTemplate.scope === 'clinic'}
                        onChange={() =>
                          setSaveAsTemplate((prev) => ({ ...prev, scope: 'clinic' }))
                        }
                        className="accent-indigo-600"
                      />
                      {t('journal.template_scope_clinic', 'All patients (clinic)')}
                    </label>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveTemplate}
                    disabled={!saveAsTemplate.title.trim() || createTemplate.isPending}
                  >
                    {createTemplate.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      t('journal.save_template_confirm', 'Save template')
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setShowSaveAsTemplate(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-6">
              <button
                type="button"
                onClick={handleOpenSaveAsTemplate}
                className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
              >
                <BookmarkPlus className="h-3.5 w-3.5" />
                {t('journal.save_as_template', 'Save as template')}
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={createEntry.isPending}
            >
              {t('common.cancel', 'Annuller')}
            </Button>
            <Button
              type="submit"
              disabled={createEntry.isPending || !content.trim()}
            >
              {createEntry.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('journal.modal.creating', 'Opretter...')}
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  {t('journal.modal.create', 'Opret journalnotat')}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
